import React, { useState, useEffect, useRef } from 'react';
import { useBreadcrumbs } from '../../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../../utils/breadcrumbUtils';
import PessoaViewModel from '../../../viewmodel/PessoaViewModel';
import { EnumBancaViewModel } from '../../../viewmodel/banca/EnumBancaViewModel';
import { Vinculo } from '../../../model/Pessoa';
import PermissaoWrapper from '../../../components/PermissaoWrapper';
import { PermissaoEnum } from '../../../model/Usuario/Permissao';
import FiltrosPessoas from './components/FiltrosPessoas';
import TabelaPessoas from './components/TabelaPessoas';
import PaginacaoPessoas from './components/PaginacaoPessoas';
import PessoaCreateModal from './PessoaCreateModal';
import HelpDrawerUsuarios from './components/HelpDrawerMembros';
import { BsQuestionCircle } from 'react-icons/bs';
import './index.css';

const MembrosCavuPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [pessoas, setPessoas] = useState({ content: [], pageMetadata: { totalElements: 0, totalPages: 0, size: 10, number: 0 } });
  const [loading, setLoading] = useState(false);
  const [showHelpDrawer, setShowHelpDrawer] = useState(false);
  const [filtros, setFiltros] = useState({ nome: '', email: '', telefone: '', vinculo: '', status: 'ATIVOS' as 'ATIVOS' | 'INATIVOS' | 'TODOS' });
  const [currentPage, setCurrentPage] = useState(0);
  const [showCriarModal, setShowCriarModal] = useState(false);
  const [vinculos, setVinculos] = useState<Vinculo[]>([]);
  const [initialized, setInitialized] = useState(false);
  const fetchPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    setBreadcrumbs(createBreadcrumbs.withoutProcessos([
      { path: `/pessoas/gerenciar-membros`, breadcrumb: 'Membros CAVU' },
    ]));
  }, [setBreadcrumbs]);

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      fetchVinculos();
      fetchPessoas();
    }
  }, [initialized]);

  // Filtros com debounce
  useEffect(() => {
    if (!initialized) return;
    
    const timer = setTimeout(() => {
      setCurrentPage(0);
      fetchPessoas(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [filtros.nome, filtros.email, filtros.telefone, filtros.vinculo, filtros.status]);

  const fetchVinculos = async () => {
    try {
      const vinculosFromApi = await EnumBancaViewModel.getVinculos();
      const vinculosFiltered = vinculosFromApi.filter(v => v.nome !== 'Advogado');
      setVinculos(vinculosFiltered as Vinculo[]);
    } catch (error) {
      console.error('Erro ao buscar vínculos:', error);
    }
  };

  const fetchPessoas = async (page = currentPage) => {
    if (fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }
    
    const fetchPromise = (async () => {
      setLoading(true);
      try {
        const filtrosLimpos: any = Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v !== ''));
        
        if (filtros.status === 'ATIVOS') {
          filtrosLimpos.mostrarInativo = false;
        } else if (filtros.status === 'INATIVOS') {
          filtrosLimpos.mostrarInativo = true;
        } else if (filtros.status === 'TODOS') {
          filtrosLimpos.mostrarInativo = true;
        }
        
        filtrosLimpos.isMembroCavu = true;
        
        const result = await PessoaViewModel.searchPessoas(filtrosLimpos, page, 10);
        setPessoas(result);
      } catch (error) {
        console.error('Erro:', error);
        setPessoas({ content: [], pageMetadata: { totalElements: 0, totalPages: 0, size: 10, number: page } });
      } finally {
        setLoading(false);
        fetchPromiseRef.current = null;
      }
    })();
    
    fetchPromiseRef.current = fetchPromise;
    return fetchPromise;
  };

  const handleFiltroChange = (campo: string, valor: string) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchPessoas(newPage);
  };

  const handleCreateSubmit = () => {
    setShowCriarModal(false);
    setCurrentPage(0);
    fetchPessoas(0);
  };

  return (
  
    <HelpDrawerUsuarios isOpen={showHelpDrawer} onClose={() => setShowHelpDrawer(false)}>
    <div className="pessoas-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Membros CAVU</h2>
        <button
          className="btn-pagination"
          onClick={() => setShowHelpDrawer(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
        >
          <BsQuestionCircle /> Ajuda
        </button>
      </div>
      <FiltrosPessoas 
        filtros={filtros}
        onFiltroChange={handleFiltroChange}
      />

      <TabelaPessoas 
        pessoas={pessoas.content}
        loading={loading}
        onPessoaUpdated={() => fetchPessoas(currentPage)}
        vinculos={vinculos}
      />
      
      <div className="pagination-container" style={{ justifyContent: 'space-between' }}>
        <PaginacaoPessoas 
          pageMetadata={pessoas.pageMetadata}
          onPageChange={handlePageChange}
        />
        
        <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CADASTRAR_PESSOA]}>
          <button 
            className="btn-pagination" 
            style={{ 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: '1px solid #28a745',
              fontWeight: '500'
            }}
            onClick={() => setShowCriarModal(true)}
          >
            + Nova Pessoa
          </button>
        </PermissaoWrapper>
      </div>

      {showCriarModal && (
        <PessoaCreateModal
          vinculos={vinculos}
          show={showCriarModal}
          handleClose={() => setShowCriarModal(false)}
          onCreate={handleCreateSubmit}
        />
      )}
    </div>
    </HelpDrawerUsuarios>
  );
};

export default MembrosCavuPage;
